import React from "react";
import PropTypes from "prop-types";
import gql from "graphql-tag";
import { useMutation } from "@apollo/client";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";

import { useAuth } from "../AuthContext";

const UPDATE_UNIT_PREFERENCES = gql`
  mutation updateUnitPreferences($input: UpdateUnitPreferencesInput!) {
    users {
      updateUnitPreferences(input: $input) {
        error {
          code
          message
        }
        user {
          id
          useMetricUnits
        }
      }
    }
  }
`;

// Three choices, not two: inherit is distinct from an explicit imperial choice.
const INHERIT = "INHERIT";
const METRIC = "METRIC";
const IMPERIAL = "IMPERIAL";

const toChoice = (useMetricUnits) => {
  if (useMetricUnits === null || useMetricUnits === undefined) return INHERIT;
  return useMetricUnits ? METRIC : IMPERIAL;
};

const toStoredValue = (choice) => {
  if (choice === INHERIT) return null;
  return choice === METRIC;
};

const EditUserUnits = ({ userId, useMetricUnits, tenantUseMetricUnits }) => {
  const [updateUnitPreferences] = useMutation(UPDATE_UNIT_PREFERENCES);
  const { setUserUseMetricUnits } = useAuth();
  const [errorMessage, setErrorMessage] = React.useState(null);

  const current = toChoice(useMetricUnits);

  const handleChange = async (_event, next) => {
    if (!next || next === current) return;

    setErrorMessage(null);
    const chosen = toStoredValue(next);

    const { data } = await updateUnitPreferences({
      variables: { input: { userId, useMetricUnits: chosen } },
    });

    const error = data?.users?.updateUnitPreferences?.error;
    if (error) {
      setErrorMessage(`${error.message} (${error.code})`);
      return;
    }

    setUserUseMetricUnits(chosen);
  };

  const inheritedLabel = tenantUseMetricUnits ? "km/h, km" : "mph, miles";

  return (
    <Stack spacing={1} sx={{ ml: 2 }}>
      <ToggleButtonGroup
        exclusive
        size="small"
        value={current}
        onChange={handleChange}
      >
        <ToggleButton value={INHERIT}>
          Company default ({inheritedLabel})
        </ToggleButton>
        <ToggleButton value={IMPERIAL}>mph, miles</ToggleButton>
        <ToggleButton value={METRIC}>km/h, km</ToggleButton>
      </ToggleButtonGroup>
      {errorMessage && <Typography color="error">{errorMessage}</Typography>}
    </Stack>
  );
};

EditUserUnits.propTypes = {
  userId: PropTypes.string.isRequired,
  useMetricUnits: PropTypes.bool,
  tenantUseMetricUnits: PropTypes.bool,
};

export default EditUserUnits;
